import {
  fundirMetadata,
  metadataModelo,
  metadataSugeridas,
  modeloDeMensaje,
  preguntasSugeridasDe,
  type PreguntasSugeridas,
} from "../../lib/preguntas";

/* ── SSE event helpers (pure, testable) ── */

/**
 * Parse a single SSE block and return the routed assistant_id, or null if the
 * block is not a well-formed `routed` event.
 */
export function parseRoutedEvent(block: string): string | null {
  let eventType = "", dataStr = "";
  for (const line of block.split("\n")) {
    if (line.startsWith("event: ")) eventType = line.slice(7).trim();
    else if (line.startsWith("data: ")) dataStr = line.slice(6);
  }
  if (eventType !== "routed" || !dataStr) return null;
  try {
    const id = JSON.parse(dataStr).assistant_id;
    return typeof id === "string" ? id : null;
  } catch {
    return null;
  }
}

export interface ClarifyCandidate {
  assistant_id: string;
  label: string;
}

/* ── SSE parser ── */

export interface SSECallbacks {
  /** Primer evento del stream: el backend ya acepto el turno y trae el id del hilo. */
  onMetadata?: (data: { thread_id?: string; run_id?: string }) => void;
  /** El guardrail bloqueo el turno (`runs.py::_blocked_stream`): el agente no lo vio. */
  onBlocked?: () => void;
  /** Evento `error`: el turno fallo a medias. */
  onError?: () => void;
  onEnd?: (data: { thread_id?: string; trace_id?: string }) => void;
  onUsage?: (data: { input?: number; output?: number; cached?: number; cost?: number }) => void;
  onRouted?: (assistantId: string) => void;
  // Provided by the component (uses i18n t()) so readSSE stays decoupled from i18n
  clarifyMessage?: (candidates: ClarifyCandidate[]) => string;
  errorMessage?: string;
}

/**
 * Parse SSE stream and yield content in assistant-ui ChatModelAdapter format.
 * Accumulates tool calls in a Map following the official pattern.
 */
export async function* readSSE(
  body: ReadableStream<Uint8Array>,
  callbacks: SSECallbacks,
  abortSignal?: AbortSignal,
) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let dirty = false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const toolCallsMap = new Map<string, any>();

  let textoAcumulado = "";

  // Formulario SUGERIDO del turno (evento `preguntas_sugeridas`). No es una part
  // del mensaje: viaja en `metadata.custom` del mensaje del asistente.
  let sugeridas: PreguntasSugeridas | null = null;

  // Modelo que genero el turno, para el badge. Llega en `response_metadata` de
  // los chunks: `langchain_openai` lo escribe en el del `finish_reason`, asi
  // que aparece cerca del final del stream y no en el primer chunk. Se queda
  // con el ULTIMO visto, que es el del mensaje que el usuario acaba leyendo.
  let modelo: string | null = null;

  // Content parts mix assistant-ui tool-call and text shapes; built loose here.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function buildContent(): any[] {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const content: any[] = [];
    for (const tc of toolCallsMap.values()) {
      content.push(tc);
    }
    if (textoAcumulado) {
      content.push({ type: "text" as const, text: textoAcumulado });
    }
    return content;
  }

  // Estado completo a emitir: parts + (si lo hay) el formulario en metadata.
  // Como funde `custom` el runtime local (@assistant-ui/core 0.2.17,
  // `runtimes/local/local-thread-runtime-core.js`, `performRoundtrip.updateMessage`):
  // un yield CON `metadata.custom` lo reescribe como `{...customPreRun,
  // ...m.metadata.custom}` —sobre el custom de ANTES del run, no sobre el del
  // yield anterior—, y un yield SIN `metadata` conserva la del mensaje tal cual.
  // Por eso cada yield que lleve `metadata.custom` debe llevar TODAS sus claves:
  // reenviar el formulario en todos los yields desde que llega es correcto por
  // construccion, no una precaucion.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function buildResult(content: any[]) {
    // Cada yield con `metadata.custom` debe llevar TODAS sus claves (ver el
    // comentario de arriba sobre como funde `custom` el runtime local), asi que
    // formulario y modelo se funden en la misma metadata en vez de escribir uno
    // u otro: emitir solo el ultimo que llego borraria el anterior.
    let metadata: { custom: Record<string, unknown> } | null = null;
    if (sugeridas) metadata = metadataSugeridas(sugeridas);
    if (modelo) metadata = fundirMetadata(metadata ?? undefined, metadataModelo(modelo));
    return metadata ? { content, metadata } : { content };
  }

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (abortSignal?.aborted) break;

      // Decode value (may be undefined on final done read)
      if (value) buffer += decoder.decode(value, { stream: true });
      // On final read, flush any remaining bytes from the decoder
      if (done) buffer += decoder.decode(undefined, { stream: false });

      const parts = buffer.split("\n\n");
      buffer = parts.pop() ?? "";

      for (const part of parts) {
        const trimmed = part.trim();
        if (!trimmed) continue;

        let eventType = "";
        let dataStr = "";
        for (const line of trimmed.split("\n")) {
          if (line.startsWith("event: ")) eventType = line.slice(7).trim();
          else if (line.startsWith("data: ")) dataStr = line.slice(6);
        }

        // `metadata` aqui es el evento del stream de LangGraph (run_id, etc.),
        // nada que ver con la `metadata` del mensaje de assistant-ui.
        if (!dataStr) continue;
        if (eventType === "metadata") {
          try { callbacks.onMetadata?.(JSON.parse(dataStr)); } catch { /* skip */ }
          continue;
        }
        if (eventType === "routed") {
          const id = parseRoutedEvent(trimmed);
          if (id) callbacks.onRouted?.(id);
          continue;
        }
        if (eventType === "clarify") {
          try {
            const candidates = (JSON.parse(dataStr).candidates ?? []) as ClarifyCandidate[];
            const text = callbacks.clarifyMessage?.(candidates);
            if (text) {
              textoAcumulado += text;
              dirty = true;
            }
          } catch { /* skip */ }
          continue;
        }
        // Formulario SUGERIDO por el backend a partir de la prosa del turno
        // (`src/preguntas_sugeridas.py`). Es una AYUDA DE INTERFAZ: el texto del
        // agente NO se toca y las preguntas se ven en los dos sitios a
        // proposito — el formulario es solo el atajo para contestarlas.
        //
        // Contrato: `{message_id, preguntas}`; solo llega si hay >= 1 pregunta y
        // antes de `end`. Al recargar el thread la MISMA forma vuelve por
        // `suggested_questions` de `GET /threads/{id}/state`, y
        // `chatApi.getThreadMessages` la deja en la misma clave de
        // `metadata.custom`, para que el componente que la pinta sea uno solo.
        if (eventType === "preguntas_sugeridas") {
          try {
            const s = preguntasSugeridasDe(JSON.parse(dataStr));
            if (s) {
              sugeridas = s;
              dirty = true;
            }
          } catch { /* skip */ }
          continue;
        }
        if (eventType === "usage") {
          try { callbacks.onUsage?.(JSON.parse(dataStr)); } catch { /* skip */ }
          continue;
        }
        if (eventType === "end") {
          try {
            const endData = JSON.parse(dataStr);
            callbacks.onEnd?.(endData);
          } catch { /* skip */ }
          continue;
        }

        if (eventType === "error") {
          textoAcumulado += callbacks.errorMessage || "An error occurred";
          dirty = true;
          callbacks.onError?.();
          continue;
        }

        try {
          const data = JSON.parse(dataStr);

          // "updates" event — extract complete tool call args from node output
          if (eventType === "updates" && typeof data === "object" && data !== null) {
            // LangGraph emits updates keyed by node name. For create_agent the
            // node is "model" (or "tools"); for custom graphs it's whatever
            // name the builder used. Flatten messages from every sub-node so
            // we always pick up the full tool_calls payload regardless of
            // graph shape.
            // Raw LangGraph stream messages — accessed by dynamic shape
            // (tool_calls, args, function.name) downstream.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const msgList: any[] = [];
            const collect = (obj: unknown) => {
              if (!obj || typeof obj !== "object") return;
              const messages = (obj as { messages?: unknown }).messages;
              if (Array.isArray(messages)) {
                for (const m of messages) msgList.push(m);
              }
            };
            if (Array.isArray(data.messages)) {
              for (const m of data.messages) msgList.push(m);
            } else {
              for (const val of Object.values(data)) collect(val);
            }
            for (const msg of msgList) {
              for (const tc of msg.tool_calls || []) {
                const id = tc.id || tc.toolCallId;
                if (!id) continue;
                const args = tc.args && Object.keys(tc.args).length > 0 ? tc.args : null;
                if (args && toolCallsMap.has(id)) {
                  const existing = toolCallsMap.get(id)!;
                  toolCallsMap.set(id, {
                    ...existing,
                    args,
                    argsText: JSON.stringify(args),
                  });
                  dirty = true;
                } else if (args && !toolCallsMap.has(id)) {
                  // Tool call not yet seen via messages — register from updates
                  const name = tc.name || tc.function?.name || "unknown";
                  toolCallsMap.set(id, {
                    type: "tool-call" as const,
                    toolCallId: id,
                    toolName: name,
                    argsText: JSON.stringify(args),
                    args,
                  });
                  dirty = true;
                }
              }
            }
            continue;
          }

          if (eventType === "messages") {
            const chunk = Array.isArray(data) ? data[0] : data;

            // Turno bloqueado por un guardrail: `runs.py::_blocked_stream` emite el
            // aviso como un mensaje del nodo `guardrail`. El texto se pinta igual;
            // el aviso es para quien necesita saber que el agente NO lo recibio.
            if (Array.isArray(data) && data[1]?.langgraph_node === "guardrail") {
              callbacks.onBlocked?.();
            }

            // Modelo del turno. Solo de mensajes del ASISTENTE: un ToolMessage
            // no declara modelo, y mirar cualquier chunk podria pillar el de un
            // subagente, que corre con otro y no es el que redacta la respuesta.
            if (!chunk?.type || chunk.type === "ai" || chunk.type === "AIMessageChunk") {
              const m = modeloDeMensaje(chunk);
              if (m) {
                modelo = m;
                dirty = true;
              }
            }

            // Track tool calls — merge args from subsequent streaming chunks
            if (chunk?.tool_calls?.length > 0) {
              for (const tc of chunk.tool_calls) {
                const name = tc.name || tc.function?.name;
                const id = tc.id || tc.toolCallId || `tc_${name}_${Date.now()}`;
                if (name && !toolCallsMap.has(id)) {
                  const args = tc.args || {};
                  toolCallsMap.set(id, {
                    type: "tool-call" as const,
                    toolCallId: id,
                    toolName: name,
                    argsText: JSON.stringify(args),
                    args,
                  });
                  dirty = true;
                } else if (id && toolCallsMap.has(id) && tc.args && Object.keys(tc.args).length > 0) {
                  const existing = toolCallsMap.get(id)!;
                  const mergedArgs = { ...existing.args, ...tc.args };
                  toolCallsMap.set(id, {
                    ...existing,
                    args: mergedArgs,
                    argsText: JSON.stringify(mergedArgs),
                  });
                  dirty = true;
                }
              }
              continue;
            }

            // Tool result messages — attach actual result
            if (chunk?.type === "tool") {
              const callId = chunk.tool_call_id;
              if (callId && toolCallsMap.has(callId)) {
                const existing = toolCallsMap.get(callId)!;
                toolCallsMap.set(callId, { ...existing, result: chunk.content });
                dirty = true;
              }
              continue;
            }

            // System messages with context summary — expose as single collapsible part
            // Use fixed ID so repeated emissions (one per ReAct step) overwrite instead of duplicating
            if (chunk?.type === "system" && typeof chunk?.content === "string" && chunk.content.includes("[CONTEXTO PREVIO")) {
              toolCallsMap.set("__context__", {
                type: "tool-call" as const,
                toolCallId: "__context__",
                toolName: "__context__",
                argsText: chunk.content,
                args: { content: chunk.content },
                result: chunk.content,
              });
              dirty = true;
              continue;
            }

            // Text content — only from AI messages (skip system/human/tool)
            const content = chunk?.content;
            if (content && typeof content === "string" && (!chunk.type || chunk.type === "ai" || chunk.type === "AIMessageChunk")) {
              textoAcumulado += content;
              dirty = true;
            }
          }
        } catch {
          // skip
        }
      }

      // Yield complete state once per network chunk
      if (dirty) {
        dirty = false;
        const content = buildContent();
        if (content.length > 0) {
          yield buildResult(content);
        }
      }

      if (done) break;
    }
  } finally {
    reader.releaseLock();
  }

  // Flush final: el bucle sale por `abortSignal` sin pasar por el yield de
  // arriba, asi que un ultimo estado pendiente se emitiria si no. Con el stream
  // completo es identico a lo ya emitido (idempotente: assistant-ui reemplaza el
  // contenido del mensaje, no lo apila).
  const finalContent = buildContent();
  if (finalContent.length > 0) {
    yield buildResult(finalContent);
  }
}
